import { FormEvent, useEffect, useMemo, useState } from "react";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { updateProfile } from "../api/auth";

export default function UserProfilePage() {
  const { user, refreshUser, logout, loading } = useAuth();
  const [fullname, setFullname] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user) {
      setFullname(user.fullname ?? "");
    }
  }, [user]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;
    setError("");
    setSuccess("");
    try {
      const payload: { fullname?: string | null; password?: string } = {};
      if (fullname !== (user.fullname ?? "")) {
        payload.fullname = fullname ? fullname : null;
      }
      if (password.trim()) {
        payload.password = password.trim();
      }
      if (Object.keys(payload).length === 0) {
        setSuccess("Нет изменений для сохранения");
        return;
      }
      setSaving(true);
      await updateProfile(user.id, payload);
      await refreshUser();
      setSuccess("Профиль успешно обновлён");
      setPassword("");
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail) && detail.length) {
        setError(detail.map((item: any) => item?.msg ?? String(item)).join("; "));
      } else {
        setError(e?.message || "Не удалось сохранить профиль");
      }
    } finally {
      setSaving(false);
    }
  };

  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60";

  const stats = useMemo(() => {
    if (!user) return [];
    return [
      { label: "Email", value: user.email },
      { label: "Компания", value: user.company_name ?? "—" },
    ];
  }, [user]);

  const skeleton = (
    <div className="space-y-4">
      <div className="h-6 w-44 animate-pulse rounded bg-slate-200" />
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="h-12 rounded-2xl bg-slate-100/80 animate-pulse" />
        ))}
      </div>
    </div>
  );

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <Topbar />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-[0_12px_45px_rgba(15,23,42,0.08)] backdrop-blur">
            {skeleton}
          </div>
        </main>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[900px] px-6 py-12 space-y-10">
        <section className="rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Профиль пользователя</h1>
              <p className="mt-2 text-sm text-slate-500">
                Ваши данные
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-red-50"
            >
              Выйти из аккаунта
            </button>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {stats.map(stat => (
              <div key={stat.label} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">{stat.label}</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </section>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_14px_38px_rgba(15,23,42,0.07)] backdrop-blur">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900">Персональные данные</h2>
              <p className="mt-1 text-sm text-slate-500">Обновите ваши данные. Для изменения email обратитесь к администратору</p>
            </div>

            <div className="space-y-5">
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">ФИО</span>
                <input
                  type="text"
                  className={baseInputClass}
                  placeholder="Введите ваше ФИО"
                  value={fullname}
                  onChange={(e) => {
                    setFullname(e.target.value);
                    if (success) setSuccess("");
                  }}
                  disabled={saving}
                />
              </label>

              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-600">Email</span>
                <input
                  type="email"
                  className={`${baseInputClass} cursor-not-allowed bg-slate-100/80 text-slate-500`}
                  value={user.email}
                  readOnly
                />
              </label>
            </div>
          </section>

          <section className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_14px_38px_rgba(15,23,42,0.07)] backdrop-blur">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900">Безопасность</h2>
              <p className="mt-1 text-sm text-slate-500">Обновите Ваш пароль, в случае необходимости</p>
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Новый пароль</span>
              <input
                type="password"
                className={baseInputClass}
                placeholder="Оставьте пустым, чтобы сохранить текущий пароль"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (success) setSuccess("");
                }}
                autoComplete="new-password"
                disabled={saving}
              />
            </label>
          </section>

          {(error || success) && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                error
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
            >
              {error || success}
            </div>
          )}

          <div className="flex justify-center items-center">
            <button
              type="submit"
              className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(99,102,241,0.25)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:w-auto"
              disabled={saving}
            >
              {saving ? "Сохранение…" : "Сохранить изменения"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
